import { useState, useEffect, useLayoutEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useLazyQuery } from '@apollo/client';
import {
    GET_DSP_OPERATIONAL_SUMMARY,
    GET_DSP_DRIVER_OPERATIONAL_LIST,
    DspOperationalSummaryResponse,
    DspDriverOperationalListResponse,
    DspDriverOperationalData,
    SortOption,
    SortOrder
} from '../pages/dsp/graphql/queries/dspOperationalData';
import { DspPath } from '../enums/DeliveryNavigatePathEnum';
import { parseLocalDate } from '../utils/dateTimeUtils';
import dayjs from 'dayjs';
import useDspWarehouseStore from '../pages/dsp/store/dspWarehouseStore';

const MAX_RETRIES = 2;
const PAGE_SIZE = 10;

/**
 * 格式化日期为API需要的格式（YYYY-MM-DD），使用本地时区避免时区问题
 */
const formatDateForAPI = (date: Date): string => {
    // 使用 dayjs 格式化，默认使用本地时区，避免 toISOString() 的 UTC 时区问题
    return dayjs(date).format('YYYY-MM-DD');
};

/**
 * DSP运营数据自定义Hook
 */
export const useDspOperationalData = () => {
    const navigate = useNavigate();
    const selectedWarehouseId = useDspWarehouseStore((state) => state.selectedWarehouseId);
    const warehouseIdVar = selectedWarehouseId ? parseInt(selectedWarehouseId) : undefined;

    // 从 URL 参数获取日期，如果没有则使用最新日期
    const getInitialDate = useCallback(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const dateParam = urlParams.get('date');

        if (dateParam) {
            const parsedDate = parseLocalDate(dateParam);
            if (parsedDate) {
                console.log('Restored date from URL parameter:', parsedDate.toISOString(), 'local:', parsedDate.toLocaleDateString());
                return parsedDate;
            } else {
                console.warn('Invalid date parameter, using current date');
            }
        }

        return new Date();
    }, []);

    // 状态管理
    const [selectedDate, setSelectedDate] = useState<Date>(getInitialDate);
    const [showCalendar, setShowCalendar] = useState(false);
    const [sortBy, setSortBy] = useState<SortOption>(SortOption.DELIVERY_RATE);
    const [sortOrder, setSortOrder] = useState<SortOrder>(SortOrder.ASC);
    const [showSortSelector, setShowSortSelector] = useState(false);

    // 分页状态
    const [currentPage, setCurrentPage] = useState(0);
    const [allDrivers, setAllDrivers] = useState<DspDriverOperationalData[]>([]);
    const [hasMore, setHasMore] = useState(true);
    const [totalDrivers, setTotalDrivers] = useState(0);

    // Refs
    const lastPageRef = useRef<number>(-1);
    const isInitialMountRef = useRef<boolean>(true);
    const prevSelectedDateRef = useRef<Date | null>(null);
    const prevSortByRef = useRef<SortOption | null>(null);
    const prevSortOrderRef = useRef<SortOrder | null>(null);
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const scrollPositionRef = useRef<number>(0);
    const retryCountMapRef = useRef<Map<number, number>>(new Map());
    const loadingPageRef = useRef<number | null>(null);
    const isLoadingRef = useRef<boolean>(false);
    const currentRequestPageRef = useRef<number | null>(null);
    const loadDriversWithRetryRef = useRef<((page: number, resetRetryCount?: boolean) => Promise<boolean>) | null>(null);
    const processedErrorRef = useRef<string | null>(null);
    const shouldRestoreScrollRef = useRef<boolean>(false);
    const prevWarehouseIdRef = useRef<number | undefined>(undefined);


    // GraphQL查询
    const {
        data: summaryData,
        loading: summaryLoading,
        refetch: refetchSummary
    } = useQuery<DspOperationalSummaryResponse>(
        GET_DSP_OPERATIONAL_SUMMARY,
        {
            variables: {
                filter: {
                    taskDate: formatDateForAPI(selectedDate),
                    warehouseId: warehouseIdVar
                }
            },
            errorPolicy: 'all'
        }
    );

    const [loadDrivers, {
        data: driversData,
        loading: driversLoading,
        error: driversError
    }] = useLazyQuery<DspDriverOperationalListResponse>(
        GET_DSP_DRIVER_OPERATIONAL_LIST,
        {
            errorPolicy: 'all',
            fetchPolicy: 'network-only'
        }
    );

    // 带重试机制的加载函数
    const loadDriversWithRetry = useCallback(async (
        page: number,
        resetRetryCount: boolean = false
    ): Promise<boolean> => {
        if (isLoadingRef.current) {
            console.log(`Another request is already loading, skipping page ${page} request`);
            return false;
        }

        if (loadingPageRef.current === page) {
            console.log(`Page ${page} is already loading, skipping duplicate request`);
            return false;
        }

        if (resetRetryCount) {
            retryCountMapRef.current.set(page, 0);
        }

        const currentRetry = retryCountMapRef.current.get(page) || 0;

        if (currentRetry > MAX_RETRIES) {
            console.error(`Max retries (${MAX_RETRIES}) already reached for page ${page}, stopping further requests`);
            return false;
        }

        loadingPageRef.current = page;
        currentRequestPageRef.current = page;
        isLoadingRef.current = true;

        if (!resetRetryCount && currentRetry > 0) {
            retryCountMapRef.current.set(page, currentRetry);
        }

        try {
            console.log(`Loading page ${page}, retry count: ${currentRetry}`);
            await loadDrivers({
                variables: {
                    filter: {
                        taskDate: formatDateForAPI(selectedDate),
                        warehouseId: warehouseIdVar
                    },
                    pageRequest: {
                        page: page,
                        size: PAGE_SIZE,
                        sortBy: sortBy,
                        sortDirection: sortOrder
                    }
                },
                fetchPolicy: 'network-only'
            });

            retryCountMapRef.current.set(page, 0);
            return true;
        } catch (error) {
            console.error(`Unexpected error loading drivers (page ${page}):`, error);
            loadingPageRef.current = null;
            currentRequestPageRef.current = null;
            isLoadingRef.current = false;
            return false;
        }
    }, [loadDrivers, selectedDate, sortBy, sortOrder, warehouseIdVar]);

    loadDriversWithRetryRef.current = loadDriversWithRetry;

    // 处理错误和重试
    useEffect(() => {
        if (driversError && currentRequestPageRef.current !== null && loadDriversWithRetryRef.current) {
            const errorId = `${currentRequestPageRef.current}-${driversError.message || 'unknown'}`;
            if (processedErrorRef.current === errorId) {
                return;
            }
            processedErrorRef.current = errorId;

            console.error('GraphQL error detected for page:', currentRequestPageRef.current, driversError);
            const failedPage = currentRequestPageRef.current;
            loadingPageRef.current = null;
            isLoadingRef.current = false;

            const currentRetry = retryCountMapRef.current.get(failedPage) || 0;
            if (currentRetry < MAX_RETRIES) {
                const nextRetry = currentRetry + 1;
                retryCountMapRef.current.set(failedPage, nextRetry);
                console.log(`Retrying page ${failedPage}, attempt ${nextRetry}/${MAX_RETRIES}`);

                setTimeout(() => {
                    if (loadDriversWithRetryRef.current && loadingPageRef.current !== failedPage) {
                        processedErrorRef.current = null;
                        loadDriversWithRetryRef.current(failedPage, false);
                    }
                }, 1000 * nextRetry);
            } else {
                console.error(`Max retries reached for page ${failedPage}, stopping further requests`);
                setHasMore(false);
                currentRequestPageRef.current = null;
                processedErrorRef.current = null;
            }
        }
    }, [driversError]);

    // 处理数据更新
    useEffect(() => {
        console.log(`[useEffect] driversData changed:`, driversData ? 'has data' : 'no data');

        if (driversData?.getDspDriverOperationalList) {
            const response = driversData.getDspDriverOperationalList;

            if (!response || !response.content || !Array.isArray(response.content)) {
                console.warn(`[useEffect] Invalid response:`, response);
                return;
            }

            console.log(`[useEffect] Response received: page=${response.page}, content.length=${response.content.length}, lastPage=${lastPageRef.current}`);

            const shouldReset = response.page === 0 && (lastPageRef.current === 0 || lastPageRef.current === -1);
            const isAppending = response.page > lastPageRef.current;

            console.log(`[useEffect] shouldReset=${shouldReset}, isAppending=${isAppending}`);

            if (!shouldReset && !isAppending) {
                console.log(`[useEffect] Skipping data update: page ${response.page}, lastPage ${lastPageRef.current}`);
                loadingPageRef.current = null;
                currentRequestPageRef.current = null;
                isLoadingRef.current = false;
                return;
            }

            loadingPageRef.current = null;
            currentRequestPageRef.current = null;
            isLoadingRef.current = false;
            processedErrorRef.current = null;

            console.log(`[useEffect] Processing data update: page=${response.page}, content.length=${response.content.length}, shouldReset=${shouldReset}, isAppending=${isAppending}`);

            if (shouldReset) {
                scrollPositionRef.current = 0;
                shouldRestoreScrollRef.current = false;
                const uniqueDrivers = response.content.reduce((acc: DspDriverOperationalData[], driver) => {
                    if (!acc.find(d => d.driverId === driver.driverId)) {
                        acc.push(driver);
                    }
                    return acc;
                }, []);

                console.log(`[useEffect] Resetting data: ${uniqueDrivers.length} unique drivers from ${response.content.length} total`);
                console.log(`[useEffect] Setting allDrivers to ${uniqueDrivers.length} drivers`);
                setAllDrivers(uniqueDrivers);
            } else if (isAppending) {
                const scrollContainer = scrollContainerRef.current;
                const savedScrollTop = scrollPositionRef.current > 0
                    ? scrollPositionRef.current
                    : (scrollContainer ? scrollContainer.scrollTop : 0);
                const savedScrollHeight = scrollContainer ? scrollContainer.scrollHeight : 0;

                scrollPositionRef.current = savedScrollTop;

                if (scrollContainer) {
                    scrollContainer.style.willChange = 'scroll-position';
                    scrollContainer.style.contain = 'layout style paint';
                }

                setAllDrivers(prev => {
                    const existingIds = new Set(prev.map(d => d.driverId));
                    const newDrivers = response.content.filter(d => !existingIds.has(d.driverId));
                    console.log(`Appending page ${response.page}: ${newDrivers.length} new drivers, total will be ${prev.length + newDrivers.length}`);
                    if (newDrivers.length === 0) {
                        return prev;
                    }
                    shouldRestoreScrollRef.current = true;
                    if (scrollContainer) {
                        (scrollContainer as any)._savedScrollHeight = savedScrollHeight;
                    }
                    return [...prev, ...newDrivers];
                });
            }

            setHasMore(response.hasNext);
            setCurrentPage(response.page);
            let totalElements = 0;
            if (typeof response.totalElements === 'string') {
                totalElements = parseInt(response.totalElements, 10);
            } else if (typeof response.totalElements === 'number') {
                totalElements = response.totalElements;
            } else if (response.totalElements != null) {
                totalElements = Number(response.totalElements) || 0;
            }
            console.log(`[useEffect] Updating totalDrivers: ${totalElements} (type: ${typeof response.totalElements}, value: ${response.totalElements})`);
            setTotalDrivers(totalElements);
            lastPageRef.current = response.page;

            retryCountMapRef.current.set(response.page, 0);
        } else if (driversData === null || driversData === undefined) {
            loadingPageRef.current = null;
            currentRequestPageRef.current = null;
            isLoadingRef.current = false;
        }
    }, [driversData]);

    // 数据更新后恢复滚动位置
    useLayoutEffect(() => {
        if (shouldRestoreScrollRef.current && scrollContainerRef.current && scrollPositionRef.current > 0) {
            const scrollContainer = scrollContainerRef.current;
            const savedPosition = scrollPositionRef.current;

            scrollContainer.scrollTo({
                top: savedPosition,
                behavior: 'auto'
            });

            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    if (scrollContainer && scrollPositionRef.current > 0) {
                        scrollContainer.scrollTo({
                            top: scrollPositionRef.current,
                            behavior: 'auto'
                        });
                        console.log(`Scroll position restored to: ${scrollPositionRef.current}`);

                        scrollContainer.style.willChange = '';
                        scrollContainer.style.contain = '';
                    }
                });
            });

            shouldRestoreScrollRef.current = false;
            const savedScrollHeight = (scrollContainer as any)._savedScrollHeight;
            if (savedScrollHeight) {
                delete (scrollContainer as any)._savedScrollHeight;
            }
        }
    }, [allDrivers.length]);

    // 后端已排序，直接使用返回的数据
    const sortedDriversData = useMemo(() => {
        if (driversLoading && allDrivers.length === 0) {
            console.log(`[sortedDriversData] Skipping computation: loading and allDrivers is empty`);
            return [];
        }

        console.log(`[sortedDriversData] Using backend sorted data: allDrivers.length=${allDrivers.length}, sortBy=${sortBy}, sortOrder=${sortOrder}, driversLoading=${driversLoading}`);

        // 后端已经排序，直接返回
        return allDrivers;
    }, [allDrivers, sortBy, sortOrder, driversLoading]);

    // 加载更多数据
    const loadMore = useCallback(async () => {
        if (!hasMore || isLoadingRef.current || driversLoading) {
            return;
        }

        const nextPage = currentPage + 1;
        console.log('loadMore called for page:', nextPage);

        if (scrollContainerRef.current) {
            scrollPositionRef.current = scrollContainerRef.current.scrollTop;
            console.log(`Saving scroll position before loading page ${nextPage}: ${scrollPositionRef.current}`);
        }

        if (loadDriversWithRetryRef.current) {
            const success = await loadDriversWithRetryRef.current(nextPage, true);

            if (!success) {
                setHasMore(false);
            }
        }
    }, [currentPage, hasMore, driversLoading]);

    // 下拉刷新
    const handleRefresh = useCallback(async () => {
        if (isLoadingRef.current) {
            console.log('Skipping refresh: already loading');
            return;
        }
        console.log('Starting refresh...');
        lastPageRef.current = -1;
        setCurrentPage(0);
        setHasMore(true);
        scrollPositionRef.current = 0;
        retryCountMapRef.current.clear();
        loadingPageRef.current = null;
        currentRequestPageRef.current = null;
        isLoadingRef.current = false;

        try {
            console.log('Refreshing summary and drivers data...');
            if (!loadDriversWithRetryRef.current) {
                console.error('loadDriversWithRetryRef.current is null!');
                return;
            }

            // 先尝试网络请求，如果失败则使用缓存
            const [summaryResult] = await Promise.allSettled([
                refetchSummary({ fetchPolicy: 'network-only' }),
                loadDriversWithRetryRef.current(0, true)
            ]);

            // 检查汇总数据请求结果
            if (summaryResult.status === 'rejected') {
                console.warn('Summary network request failed, retrying with cache-first policy:', summaryResult.reason);
                try {
                    await refetchSummary({ fetchPolicy: 'cache-first' });
                } catch (cacheError) {
                    console.error('Summary cache request also failed:', cacheError);
                }
            }

            console.log('Refresh API calls completed, waiting for data update...');
        } catch (error) {
            console.error('Refresh failed:', error);
            loadingPageRef.current = null;
            currentRequestPageRef.current = null;
            isLoadingRef.current = false;
        }
    }, [refetchSummary]);

    // 日期选择处理
    const handleDateSelect = useCallback((date: Date | null) => {
        if (date) {
            setSelectedDate(date);
            setShowCalendar(false);
            // 更新 URL 参数，保持日期状态
            const searchParams = new URLSearchParams(window.location.search);
            searchParams.set('date', formatDateForAPI(date));
            navigate(`${window.location.pathname}?${searchParams.toString()}`, { replace: true });
        }
    }, [navigate]);

    // 排序处理
    const handleSort = useCallback((newSortBy: SortOption) => {
        // 根据排序维度设置排序方向
        // 妥投率：由低到高（ASC）
        // 领取件量、派送失败量：由高到低（DESC）
        const newSortOrder = newSortBy === SortOption.DELIVERY_RATE 
            ? SortOrder.ASC 
            : SortOrder.DESC;
        
        setSortBy(newSortBy);
        setSortOrder(newSortOrder);
        setShowSortSelector(false);
    }, []);

    // 跳转到详情页
    const handleViewDetails = useCallback(() => {
        const searchParams = new URLSearchParams();
        searchParams.set('date', formatDateForAPI(selectedDate));
        navigate(`${DspPath.DELIVERY_DSP_OPERATIONAL_PARTICULARS}?${searchParams.toString()}`);
    }, [navigate, selectedDate]);

    // 跳转到司机包裹详情页
    const handleViewDriverPackages = useCallback((driverId: string | number) => {
        const basePath = DspPath.DELIVERY_DSP_OPERATIONAL_PARTICULARS
            .replace(':driverId', driverId.toString());

        const searchParams = new URLSearchParams();
        searchParams.set('date', formatDateForAPI(selectedDate));

        navigate(`${basePath}?${searchParams.toString()}`);
    }, [navigate, selectedDate]);

    // 初始加载和日期/排序/仓库改变时重新获取数据
    useEffect(() => {
        const dateChanged = prevSelectedDateRef.current !== null &&
            formatDateForAPI(prevSelectedDateRef.current) !== formatDateForAPI(selectedDate);
        const sortChanged = prevSortByRef.current !== null &&
            (prevSortByRef.current !== sortBy || prevSortOrderRef.current !== sortOrder);
        const warehouseChanged = prevWarehouseIdRef.current !== undefined &&
            prevWarehouseIdRef.current !== warehouseIdVar;

        if (isInitialMountRef.current) {
            isInitialMountRef.current = false;
            prevSelectedDateRef.current = selectedDate;
            prevSortByRef.current = sortBy;
            prevSortOrderRef.current = sortOrder;
            prevWarehouseIdRef.current = warehouseIdVar;
            lastPageRef.current = -1;
            retryCountMapRef.current.clear();
            if (loadDriversWithRetryRef.current) {
                loadDriversWithRetryRef.current(0, true);
            }
        } else if (dateChanged || sortChanged || warehouseChanged) {
            prevSelectedDateRef.current = selectedDate;
            prevSortByRef.current = sortBy;
            prevSortOrderRef.current = sortOrder;
            prevWarehouseIdRef.current = warehouseIdVar;
            lastPageRef.current = -1;
            if (dateChanged || warehouseChanged) {
                refetchSummary();
            }
            setCurrentPage(0);
            setAllDrivers([]);
            setHasMore(true);
            setTotalDrivers(0);
            scrollPositionRef.current = 0;
            retryCountMapRef.current.clear();
            if (loadDriversWithRetryRef.current) {
                loadDriversWithRetryRef.current(0, true);
            }
        }
    }, [selectedDate, refetchSummary, sortBy, sortOrder, warehouseIdVar]);

    return {
        // 状态
        selectedDate,
        showCalendar,
        setShowCalendar,
        sortBy,
        sortOrder,
        showSortSelector,
        setShowSortSelector,
        summaryData,
        summaryLoading,
        driversLoading,
        sortedDriversData,
        totalDrivers,
        hasMore,
        currentPage,

        // Refs
        scrollContainerRef,

        // 方法
        handleDateSelect,
        handleSort,
        handleRefresh,
        loadMore,
        handleViewDetails,
        handleViewDriverPackages
    };
};

